import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import WizardPageWrapper, { GeneralPageSummary } from "../WizardPageWrapper";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  InputNumber,
  List,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { getStateList } from "../../../../../../store/actions/adminActionCreators";
import {
  CoverWizardForm,
  emptyCoverQuoteInitialValues,
  FlowType,
  StateCode,
} from "../../CoverWizardModal";
import { poolOwnerInfoPageContainsEmptyFields } from "../../validation-utils";
import ConfirmationModal from "../../../../../../components/ConfirmationModal";
import LeaveWizardConfirmationModal from "../LeaveWizardConfirmationModal";
import Flex from "../../../../../../components/Flex";
import OrderDto from "../../../../../../store/actions/dtos/OrderDto";
import {
  getAllAdminOrder,
  getAllOrder,
} from "../../../../../../store/actions/orderActionCreators";
import { getUserFromStorage } from "../../../../../../util/localStorage.util";
import { debounce } from "lodash";
import {
  CloseOutlined,
  SearchOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { getOrderDetails } from "../../../../../../store/actions/orderFlowActionCreators";
import { payloadToFormModel } from "../../../OrderContextMenu";

type SelectOrderPageProps = {
  wizardPageNumberState: [number, React.Dispatch<React.SetStateAction<number>>];
  onNextButtonClick: () => void;
  onBackButtonClick?: () => void;
  hideBackButton?: boolean;
};
const orderTypes: Array<string> = [
  "Return",
  "Replacement",
  "Repair",
  "Alteration",
];

export default function SelectOrderPage({
  wizardPageNumberState: [wizardPageNumber, setWizardPageNumber],
  onNextButtonClick,
  onBackButtonClick,
  hideBackButton = false,
}: SelectOrderPageProps) {
  const form = Form.useFormInstance<CoverWizardForm>();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [orderBy, setOrderBy] = useState<string>("");
  const [orderDirection, setOrderDirection] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [orders, setOrders] = useState<OrderDto[]>([]);
  const storageInfo = getUserFromStorage();
  const [totalPage, setTotalPage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    let _orderBy = sorter.column ? sorter.field : "";
    let _orderDirection = "";
    if (sorter.column) {
      if (sorter.order === "descend") {
        _orderDirection = "desc";
      } else {
        _orderDirection = "asc";
      }
    }
    setOrderBy(orderBy);
    setOrderDirection(orderDirection);
    setCurrentPage(pagination.current);
    getOrders(searchTerm, pagination.current, _orderBy, _orderDirection);
  };

  useEffect(() => {
    getOrders(searchTerm, currentPage, orderBy, orderDirection);
  }, []);

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      getOrders(term, currentPage, orderBy, orderDirection);
    }, 500), // 500ms delay
    []
  );

  async function getOrders(
    term: string,
    pageNumber: number,
    _orderBy: string,
    _orderDirection: string
  ) {
    setLoading(true);
    const data = {
      PageNumber: pageNumber,
      PageSize: 5,
      OrderStartDate: "",
      OrderEndDate: "",
      ShipStartDate: "",
      ShipEndDate: "",
      SearchByJobTitle: "",
      SearchBySoNo: term,
      SearchByPoNo: "",
      SearchByMeycoNo: "",
      OrderBy: `${_orderBy} ${_orderDirection}`,
      SearchByGroups: "",
    };
    const [response, err]: Array<any> = storageInfo
      ? JSON.parse(storageInfo).isAdmin
        ? await getAllAdminOrder(data)
        : await getAllOrder(data)
      : await getAllOrder(data);
    if (!!response) {
      setOrders(response.data);
      setTotalPage(response.totalPages);
    }
    setLoading(false);
  }

  async function autofillOrderInfo(soNum: string) {
    const existingValues = form.getFieldsValue(true);
    const [orderDetails, orderDetailsError] = await getOrderDetails(soNum);
    if (!orderDetails || orderDetailsError) return;
    const mappedPayload = payloadToFormModel(orderDetails.data);
    form.setFieldsValue({
      ...mappedPayload,
      quoteType: existingValues.quoteType,
      soNum: soNum,
    });
  }

  function handleClearSelectedOrder() {
    const existingValues = form.getFieldsValue(true);
    form.setFieldsValue({
      ...emptyCoverQuoteInitialValues,
      quoteType: existingValues.quoteType,
    });
  }

  const columns = [
    {
      title: "SO#",
      dataIndex: "soNo",
      sorter: true,
    },
    {
      title: "PO#",
      dataIndex: "poNo",
      sorter: true,
    },
    {
      title: "Meyco#",
      dataIndex: "meycoNo",
      sorter: true,
    },
    {
      title: "Job Name",
      dataIndex: "jobName",
      sorter: true,
    },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      sorter: true,
    },
  ];

  const selectedOrderSono = Form.useWatch("soNum", { form, preserve: true });

  return (
    <>
      <WizardPageWrapper
        title="Autofill from an Order"
        summary="Select an order to autofill the order information with."
        onNextButtonClick={onNextButtonClick}
        onBackButtonClick={onBackButtonClick}
        disableNext={!selectedOrderSono}
        hideBackButton={hideBackButton}
      >
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <Flex vertical gap={8}>
              <Flex
                vertical
                gap={12}
                style={{ width: "100%", overflowX: "auto" }}
              >
                <Flex
                  gap={16}
                  justify="space-between"
                  align="center"
                  style={{ width: "100%" }}
                >
                  <Input
                    style={{ maxWidth: "200px" }}
                    prefix={<SearchOutlined />}
                    placeholder="Search by SO#"
                    value={searchTerm}
                    allowClear
                    onChange={(event) => {
                      const searchTerm = event.target.value;
                      debouncedSearch(searchTerm);
                      setSearchTerm(searchTerm);
                    }}
                  />
                  {!!selectedOrderSono ? (
                    <Flex gap={4} align="center">
                      <Typography.Text
                        style={{
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        *Selected Order:{" "}
                      </Typography.Text>
                      <Flex
                        align="center"
                        style={{
                          backgroundColor: "#5a83d3",
                          padding: "0 8px 0 12px",
                          borderRadius: "20px",
                        }}
                      >
                        <TableOutlined style={{ marginRight: "6px" }} />
                        <Typography.Text style={{ whiteSpace: "nowrap" }}>
                          {!!selectedOrderSono ? selectedOrderSono : "None"}
                        </Typography.Text>
                        <Button
                          style={{ border: "none" }}
                          onClick={handleClearSelectedOrder}
                          icon={<CloseOutlined style={{ fontSize: "12px" }} />}
                          size="small"
                        />
                      </Flex>
                    </Flex>
                  ) : (
                    <Flex vertical>
                      <Typography.Text italic style={{ color: "orange" }}>
                        No Order Selected
                      </Typography.Text>
                      <a
                        onClick={() => setWizardPageNumber(2)}
                        style={{ border: "unset" }}
                        type="text"
                      >
                        <i>Skip ahead with blank order</i>
                      </a>
                    </Flex>
                  )}
                </Flex>
                <Table<OrderDto>
                  loading={loading}
                  rowKey={(record) => record.soNo}
                  rowSelection={{
                    hideSelectAll: true,
                    type: "checkbox",
                    selectedRowKeys: selectedOrderSono
                      ? [selectedOrderSono]
                      : [],
                    onChange: (_, selectedRows) => {
                      if (!!selectedRows?.length) {
                        // Autofill order info
                        const newSoNum =
                          selectedRows?.[1]?.soNo ?? selectedRows?.[0]?.soNo;
                        autofillOrderInfo(newSoNum);
                      } else {
                        // Reset form to initial values
                        handleClearSelectedOrder();
                      }
                    },
                  }}
                  onChange={handleTableChange}
                  pagination={{
                    defaultPageSize: 20,
                    total: totalPage,
                    showSizeChanger: false,
                  }}
                  dataSource={orders}
                  columns={columns}
                />
              </Flex>
            </Flex>
          </Col>
        </Row>
      </WizardPageWrapper>
    </>
  );
}
